import { useAuth } from '../auth/AuthContext';
import { Logotype } from '../components/Wordmark';

/**
 * What a signed-in account with no store sees.
 *
 * An account is a person and a grant is one store, so removing someone from
 * their last store leaves a login that opens onto nothing. That is deliberate —
 * suspending the account instead would let one store's owner lock a person out
 * of every other store they work at. But the shell has nothing to show without
 * a store, and rendered as a console with every section empty it read as the
 * app being broken rather than as "you no longer have access".
 */
export function NoStores(): React.JSX.Element {
  const { user, logout } = useAuth();

  return (
    <div className="flex min-h-dvh items-center justify-center bg-paper px-4 py-10">
      <div className="w-full max-w-[22rem]">
        <div className="mb-7 text-center">
          <Logotype size="lg" />
          <p className="qs-eyebrow mt-2">Cloud</p>
        </div>

        <div className="qs-panel overflow-hidden p-0">
          <div className="qs-brand-rule h-1" aria-hidden />
          <div className="p-5">
            <h1 className="text-lg font-medium tracking-tight">No stores to show</h1>
            <p className="mt-2 text-sm text-ink-muted">
              You are signed in as <span className="wrap-anywhere text-ink">{user?.email}</span>,
              but this account does not have access to any store.
            </p>
            <p className="mt-2 text-sm text-ink-muted">
              If it should, ask the store's owner to add you from their Access page, then check
              again.
            </p>

            {/* A full load rather than a refetch: the app starts again from
                /auth/me, which also notices a session that ended meanwhile. */}
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="qs-brand-rule mt-5 block w-full rounded px-3 py-2 text-center font-medium text-[#080d16] transition-opacity hover:opacity-90"
            >
              Check again
            </button>
            <button
              type="button"
              onClick={() => void logout()}
              className="mt-2 w-full rounded border border-rule px-3 py-2 text-sm text-ink-muted hover:text-ink"
            >
              Sign out
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
